import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  QrCode, ArrowLeft, Wrench, Lock, HelpCircle, Loader2, Check, Eye, EyeOff, Calendar, MapPin, User, Phone
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getIdToken } from "../lib/firebase";

const API = process.env.REACT_APP_BACKEND_URL;

const securityQuestions = [
  "What is your mother's maiden name?",
  "What was the name of your first pet?",
  "What city were you born in?",
  "What is your favorite movie?",
  "What was your childhood nickname?",
];

const statusOptions = [
  { value: "working", label: "Working" },
  { value: "under_maintenance", label: "Under Maintenance" },
  { value: "not_working", label: "Not Working" },
];

const QRAssignPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: machine info, 2: instructions, 3: security
  const [formData, setFormData] = useState({
    // Basic Info
    machine_name: "",
    machine_id: "",
    machine_type: "",
    location: "",
    installation_date: "",
    // Responsibility
    operator_name: "",
    technician_name: "",
    contact_number: "",
    // Status
    current_status: "working",
    last_service_date: "",
    next_service_due: "",
    // Instructions
    operating_instructions: "",
    safety_precautions: "",
    // Maintenance Logs (start empty)
    maintenance_logs: [],
  });
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Validation function to check required fields
  const isStep1Valid = () => {
    return formData.machine_name && formData.machine_name.trim().length > 0;
  };

  const handleStep1Continue = () => {
    if (!isStep1Valid()) {
      toast.error("Please enter the Machine Name (required)");
      return;
    }
    setStep(2);
  };

  const handleStep2Continue = () => {
    setStep(3);
  };

  const handleSubmit = async () => {
    // Validate PIN
    if (pin.length < 4 || pin.length > 6 || !/^\d+$/.test(pin)) {
      toast.error("PIN must be 4-6 digits");
      return;
    }
    if (pin !== confirmPin) {
      toast.error("PINs do not match");
      return;
    }
    if (!securityQuestion || !securityAnswer) {
      toast.error("Please set a security question and answer");
      return;
    }
    if (!consentChecked) {
      toast.error("Please acknowledge the information visibility disclosure");
      return;
    }

    setLoading(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API}/api/qr/${code}/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          purpose: "machine",
          data: formData,
          pin,
          security_question: securityQuestion,
          security_answer: securityAnswer,
          contact_visibility_consent: true,
          consent_timestamp: new Date().toISOString(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to claim QR code");
      }

      toast.success("QR code assigned to machine successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-6">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <p className="text-sm text-[#6B7280]">Assign QR to Machine</p>
            <p className="font-bold text-[#1F2933] font-mono">{code}</p>
          </div>
        </div>
        
        {/* Progress */}
        <div className="flex gap-2 mt-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex-1 h-1 rounded-full ${
                s <= step ? "bg-[#2563EB]" : "bg-gray-200"
              }`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-[#6B7280]">
          <span>Machine Info</span>
          <span>Instructions</span>
          <span>Security</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Step 1: Machine Basic Info & Responsibility */}
        {step === 1 && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#64748B]/10 rounded-xl flex items-center justify-center">
                <Wrench className="w-6 h-6 text-[#64748B]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#1F2933]" style={{ fontFamily: 'Manrope' }}>
                  Machine Details
                </h2>
                <p className="text-sm text-[#6B7280]">Enter machine information</p>
              </div>
            </div>
            
            {/* Basic Info Card */}
            <Card className="bg-white shadow-sm mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-[#2563EB]" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  label="Machine Name"
                  placeholder="e.g., CNC Lathe Machine"
                  value={formData.machine_name}
                  onChange={(v) => handleInputChange("machine_name", v)}
                  required
                />
                <FormField
                  label="Machine ID / Number"
                  placeholder="e.g., MCH-001"
                  value={formData.machine_id}
                  onChange={(v) => handleInputChange("machine_id", v)}
                />
                <FormField
                  label="Machine Type"
                  placeholder="e.g., CNC, Lathe, Press, Compressor"
                  value={formData.machine_type}
                  onChange={(v) => handleInputChange("machine_type", v)}
                />
                <FormField
                  label="Location"
                  placeholder="e.g., Building A, Floor 2, Bay 3"
                  value={formData.location}
                  onChange={(v) => handleInputChange("location", v)}
                  icon={MapPin}
                />
                <FormField
                  label="Installation Date"
                  placeholder="DD/MM/YYYY"
                  value={formData.installation_date}
                  onChange={(v) => handleInputChange("installation_date", v)}
                  icon={Calendar}
                />
              </CardContent>
            </Card>

            {/* Responsibility Card */}
            <Card className="bg-white shadow-sm mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-[#2563EB]" />
                  Responsibility
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  label="Operator Name"
                  placeholder="Enter operator's name"
                  value={formData.operator_name}
                  onChange={(v) => handleInputChange("operator_name", v)}
                />
                <FormField
                  label="Technician Name"
                  placeholder="Enter technician's name"
                  value={formData.technician_name}
                  onChange={(v) => handleInputChange("technician_name", v)}
                />
                <FormField
                  label="Contact Number"
                  placeholder="Enter contact number"
                  value={formData.contact_number}
                  onChange={(v) => handleInputChange("contact_number", v)}
                  icon={Phone}
                />
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-[#2563EB]" />
                  Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Status</Label>
                  <Select 
                    value={formData.current_status} 
                    onValueChange={(v) => handleInputChange("current_status", v)}
                  >
                    <SelectTrigger className="h-12" data-testid="status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FormField
                  label="Last Service Date"
                  placeholder="DD/MM/YYYY"
                  value={formData.last_service_date}
                  onChange={(v) => handleInputChange("last_service_date", v)}
                  icon={Calendar}
                />
                <FormField
                  label="Next Service Due"
                  placeholder="DD/MM/YYYY"
                  value={formData.next_service_due}
                  onChange={(v) => handleInputChange("next_service_due", v)}
                  icon={Calendar}
                />
              </CardContent>
            </Card>

            <Button
              onClick={handleStep1Continue}
              disabled={!isStep1Valid()}
              className="w-full h-12 mt-6 bg-[#2563EB] hover:bg-[#1D4ED8]"
              data-testid="next-step-btn"
            >
              Continue to Instructions
            </Button>
          </div>
        )}

        {/* Step 2: Instructions & Precautions */}
        {step === 2 && (
          <div>
            <h2 className="text-lg font-semibold text-[#1F2933] mb-4" style={{ fontFamily: 'Manrope' }}>
              Instructions & Precautions
            </h2>
            
            <Card className="bg-white shadow-sm mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-[#16A34A]">
                  Operating Instructions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Enter step-by-step operating instructions for this machine...

Example:
1. Check oil level before starting
2. Warm up for 5 minutes
3. Set speed according to material
4. Always use safety guards"
                  value={formData.operating_instructions}
                  onChange={(e) => handleInputChange("operating_instructions", e.target.value)}
                  className="min-h-[150px]"
                  data-testid="operating-instructions"
                />
              </CardContent>
            </Card>

            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-[#DC2626]">
                  Safety Precautions / Do's & Don'ts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Enter safety precautions and warnings...

Example:
DO's:
✓ Wear safety goggles
✓ Use ear protection
✓ Keep area clean

DON'Ts:
✗ Never operate without training
✗ Do not bypass safety guards
✗ Avoid loose clothing near moving parts"
                  value={formData.safety_precautions}
                  onChange={(e) => handleInputChange("safety_precautions", e.target.value)}
                  className="min-h-[150px]"
                  data-testid="safety-precautions"
                />
              </CardContent>
            </Card>

            <Button
              onClick={handleStep2Continue}
              className="w-full h-12 mt-6 bg-[#2563EB] hover:bg-[#1D4ED8]"
              data-testid="next-security-btn"
            >
              Continue to Security Setup
            </Button>
          </div>
        )}

        {/* Step 3: Security Setup */}
        {step === 3 && (
          <div>
            <h2 className="text-lg font-semibold text-[#1F2933] mb-4" style={{ fontFamily: 'Manrope' }}>
              Set Up Security
            </h2>

            <Card className="bg-white shadow-sm mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#2563EB]" />
                  Set PIN (4-6 digits)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-[#6B7280]">
                  This PIN will be required to edit machine details or add maintenance logs.
                </p>
                <div className="space-y-2">
                  <Label>PIN</Label>
                  <div className="relative">
                    <Input
                      type={showPin ? "text" : "password"}
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="••••••"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                      className="h-12 text-center text-xl tracking-widest pr-10"
                      data-testid="pin-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPin(!showPin)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="pin-toggle"
                    >
                      {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Confirm PIN</Label>
                  <div className="relative">
                    <Input
                      type={showConfirmPin ? "text" : "password"}
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="••••••"
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                      className="h-12 text-center text-xl tracking-widest pr-10"
                      data-testid="confirm-pin-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPin(!showConfirmPin)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="confirm-pin-toggle"
                    >
                      {showConfirmPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {pin && confirmPin && pin === confirmPin && (
                  <div className="flex items-center gap-2 text-[#16A34A]">
                    <Check className="w-4 h-4" />
                    <span className="text-sm">PINs match</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-[#2563EB]" />
                  Security Question
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Question</Label>
                  <Select value={securityQuestion} onValueChange={setSecurityQuestion}>
                    <SelectTrigger className="h-12" data-testid="security-question-select">
                      <SelectValue placeholder="Choose a question" />
                    </SelectTrigger>
                    <SelectContent>
                      {securityQuestions.map((q) => (
                        <SelectItem key={q} value={q}>{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Your Answer</Label>
                  <Input
                    placeholder="Enter your answer"
                    value={securityAnswer}
                    onChange={(e) => setSecurityAnswer(e.target.value)}
                    className="h-12"
                    data-testid="security-answer-input"
                  />
                  <p className="text-xs text-[#9CA3AF]">
                    This will be used to recover your PIN if forgotten
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Information Visibility Disclosure */}
            <Card className="bg-[#FEF3C7] border-[#F59E0B]/30 shadow-sm mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-[#92400E]">
                  <Eye className="w-4 h-4" />
                  Information Visibility Notice
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-[#78350F] leading-relaxed">
                  When someone scans this QR code, they will be able to view the machine information, 
                  maintenance history, operating instructions, and safety precautions you provide.
                </p>
                <div className="flex items-start gap-3 p-3 bg-white/60 rounded-lg">
                  <Checkbox
                    id="consent-checkbox"
                    checked={consentChecked}
                    onCheckedChange={setConsentChecked}
                    className="mt-0.5 border-[#92400E] data-[state=checked]:bg-[#2563EB] data-[state=checked]:border-[#2563EB]"
                    data-testid="consent-checkbox"
                  />
                  <label 
                    htmlFor="consent-checkbox" 
                    className="text-sm text-[#78350F] cursor-pointer leading-relaxed"
                  >
                    I understand that the machine details and maintenance information will be visible 
                    to anyone who scans this QR code.
                  </label>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleSubmit}
              disabled={loading || !pin || !confirmPin || pin !== confirmPin || !securityQuestion || !securityAnswer || !consentChecked}
              className="w-full h-12 mt-6 bg-[#16A34A] hover:bg-[#15803D]"
              data-testid="complete-setup-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Check className="w-5 h-5 mr-2" />
              )}
              Complete Setup
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const FormField = ({ label, placeholder, value, onChange, required, multiline, helper, icon: Icon }) => (
  <div className="space-y-2">
    <Label>
      {label}
      {required && <span className="text-[#DC2626] ml-1">*</span>}
    </Label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
      )}
      {multiline ? (
        <Textarea
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[80px]"
        />
      ) : (
        <Input
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`h-12 ${Icon ? 'pl-10' : ''}`}
        />
      )}
    </div>
    {helper && (
      <p className="text-xs text-[#9CA3AF]">{helper}</p>
    )}
  </div>
);

export default QRAssignPage;
